import React, { useEffect, useState } from "react";
import {
  ExternalBarcodeLookupResponse,
  Product,
  ProductItemWithDetails,
} from "utils/grocyApi.ts";
import {
  useProductsQuery,
  useQuantityUnitsQuery,
  useShoppingListQuery,
  useUserSettingsQuery,
} from "hooks/useQueries.ts";
import { usePurchaseForm } from "../../hooks/usePurchaseForm.ts";
import { usePurchaseLogic } from "../../hooks/usePurchaseLogic.ts";
import { CreationMode } from "../../types/purchase.ts";
import PurchaseModal from "./PurchaseModal.tsx";
import PurchaseFormFields from "./PurchaseFormFields.tsx";
import ProductGroupSelectorWithAI from "./ProductGroupSelectorWithAI.tsx";
import PurchaseButton from "./PurchaseButton.tsx";
import ProductImage from "./ProductImage.tsx";
import AmountInput from "./AmountInput.tsx";
import ParentOrLinkSelector from "./ParentOrLinkSelector.tsx";
import { ExpiryExtractionResult } from "utils/aiExpiryDate.ts";
import { getUnitName } from "utils/quantityUnitHelpers.ts";
import {
  suggestUnknownProductAction,
  UnknownProductSuggestionResult,
} from "utils/unknownProductSuggestion.ts";
import "./PurchaseControls.css";

export interface PurchaseControlsProps {
  existingProduct?: ProductItemWithDetails;
  unknownProduct?: ExternalBarcodeLookupResponse;
  onClear: () => void;
  onRequestPhotoCapture?: () => Promise<ExpiryExtractionResult | null>;
}

const PurchaseControls: React.FC<PurchaseControlsProps> = (
  { existingProduct, unknownProduct, onClear, onRequestPhotoCapture },
) => {
  const { data: userSettings } = useUserSettingsQuery();
  const { data: products } = useProductsQuery();
  const { data: shoppingListItems = [] } = useShoppingListQuery();
  const { data: quantityUnits = [] } = useQuantityUnitsQuery();
  const {
    amount,
    setAmount,
    expiryDate,
    setExpiryDate,
    locationId,
    setLocationId,
    computeExpiryFromDefaultDays,
  } = usePurchaseForm({
    locationId: existingProduct
      ? existingProduct.product.location_id
      : (unknownProduct ? unknownProduct.location_id : null),
  });
  const { handleScannedPurchase, isLoading } = usePurchaseLogic({
    onComplete: onClear,
  });

  const [productGroupId, setProductGroupId] = useState<number | null>(null);
  const [parentProductId, setParentProductId] = useState<number | null>(null);
  const [parentProductSearchTerm, setParentProductSearchTerm] = useState<
    string
  >("");

  // Unknown barcode handling: create vs link
  const [creationMode, setCreationMode] = useState<CreationMode>(
    CreationMode.Create,
  );
  const [linkProductId, setLinkProductId] = useState<number | null>(null);
  const [linkProductSearchTerm, setLinkProductSearchTerm] = useState<string>(
    "",
  );
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<
    UnknownProductSuggestionResult | null
  >(null);
  const [aiDismissed, setAiDismissed] = useState(false);
  const [aiResolvedScanKey, setAiResolvedScanKey] = useState<string | null>(
    null,
  );

  // Update location and product group with preset values when userSettings loads
  useEffect(() => {
    if (userSettings && !existingProduct) {
      const presetLocationId = userSettings.product_presets_location_id;
      if (presetLocationId && !isNaN(Number(presetLocationId))) {
        setLocationId(Number(presetLocationId));
      }
    }
  }, [userSettings, existingProduct, setLocationId]);

  useEffect(() => {
    if (userSettings && unknownProduct) {
      const presetProductGroupId =
        userSettings.product_presets_product_group_id;
      if (presetProductGroupId && !isNaN(Number(presetProductGroupId))) {
        setProductGroupId(Number(presetProductGroupId));
      }
    }
  }, [userSettings, unknownProduct]);

  // Auto-fill for existing scanned product
  useEffect(() => {
    if (existingProduct) {
      setLocationId(existingProduct.product.location_id);
      // Attempt to find full product to get parent_product_id
      const fullProduct = products?.find((p) =>
        p.id === existingProduct.product.id
      );
      const parentId = fullProduct?.parent_product_id ?? null;
      setParentProductId(parentId);
      // Clear search term since parent is already selected
      setParentProductSearchTerm("");
      setExpiryDate(
        computeExpiryFromDefaultDays(
          existingProduct.product.default_best_before_days,
        ),
      );
    }
  }, [existingProduct, products]);

  // Auto-fill for unknown product from external lookup (location available, no default days)
  useEffect(() => {
    if (unknownProduct) {
      setLocationId(unknownProduct.location_id);
      // No parent or expiry data available here
      setParentProductSearchTerm("");
      setParentProductId(null);
      // Reset link/create state for a fresh scan
      setCreationMode(CreationMode.Create);
      setLinkProductId(null);
      setLinkProductSearchTerm("");
      setAiSuggestion(null);
      setAiDismissed(false);
      setAiResolvedScanKey(null);
    }
  }, [unknownProduct]);

  useEffect(() => {
    if (!unknownProduct || !products?.length) {
      setAiLoading(false);
      return;
    }

    if (aiDismissed) {
      setAiLoading(false);
      return;
    }

    const scanKey = `${unknownProduct.__barcode}:${unknownProduct.name}`;
    if (aiResolvedScanKey === scanKey) {
      setAiLoading(false);
      return;
    }

    let cancelled = false;

    setAiLoading(true);

    const applyProductDefaults = (product: Product, mode: CreationMode) => {
      setLocationId(product.location_id || null);
      if (mode === CreationMode.Parent) {
        setProductGroupId(product.product_group_id || null);
      }
      if (
        typeof product.default_best_before_days === "number" &&
        product.default_best_before_days !== 0
      ) {
        setExpiryDate(
          computeExpiryFromDefaultDays(product.default_best_before_days),
        );
      }
    };

    suggestUnknownProductAction(unknownProduct, products, shoppingListItems)
      .then((suggestion) => {
        if (cancelled) return;

        setAiResolvedScanKey(scanKey);
        setAiSuggestion(suggestion);
        if (!suggestion || aiDismissed) {
          return;
        }

        if (suggestion.mode === CreationMode.Create) {
          setCreationMode(CreationMode.Create);
          setParentProductId(null);
          setParentProductSearchTerm("");
          setLinkProductId(null);
          setLinkProductSearchTerm("");
          return;
        }

        const suggestedProduct = products.find((product) =>
          product.id === suggestion.productId
        );
        if (!suggestedProduct) {
          setCreationMode(CreationMode.Create);
          return;
        }

        if (suggestion.mode === CreationMode.Parent) {
          setCreationMode(CreationMode.Parent);
          setParentProductId(suggestedProduct.id);
          setParentProductSearchTerm("");
          setLinkProductId(null);
          setLinkProductSearchTerm("");
          applyProductDefaults(suggestedProduct, CreationMode.Parent);
          return;
        }

        if (suggestion.mode === CreationMode.Link) {
          setCreationMode(CreationMode.Link);
          setLinkProductId(suggestedProduct.id);
          setLinkProductSearchTerm("");
          setParentProductId(null);
          setParentProductSearchTerm("");
          applyProductDefaults(suggestedProduct, CreationMode.Link);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setAiLoading(false);
        }
      });

    return () => {
      cancelled = true;
      setAiLoading(false);
    };
  }, [
    unknownProduct,
    products,
    shoppingListItems,
    setLocationId,
    computeExpiryFromDefaultDays,
    aiDismissed,
    aiResolvedScanKey,
  ]);

  const handleDismissAISuggestion = () => {
    setAiDismissed(true);
  };

  const aiSuggestionProductName = aiSuggestion?.productId
    ? products?.find((product) => product.id === aiSuggestion.productId)
      ?.name ?? null
    : null;

  const handleCreationModeChange = (nextMode: CreationMode) => {
    const suggestedProductId = aiSuggestion?.productId ?? null;

    if (nextMode === CreationMode.Link) {
      const preservedProductId = linkProductId ?? parentProductId ??
        suggestedProductId;
      if (preservedProductId) {
        setLinkProductId(preservedProductId);
        setLinkProductSearchTerm("");
      }
    }

    if (nextMode === CreationMode.Parent) {
      const preservedProductId = parentProductId ?? linkProductId ??
        suggestedProductId;
      if (preservedProductId) {
        setParentProductId(preservedProductId);
        setParentProductSearchTerm("");
      }
    }

    setCreationMode(nextMode);
  };

  const handlePurchase = async () => {
    await handleScannedPurchase(
      existingProduct,
      unknownProduct,
      creationMode,
      linkProductId,
      linkedProduct?.location_id ?? null,
      amount,
      expiryDate,
      locationId,
      parentProductId,
      productGroupId,
    );
  };

  const presetQuantityUnitId = Number(userSettings?.product_presets_qu_id) ||
    null;
  const linkedProduct = linkProductId
    ? products?.find((product) => product.id === linkProductId)
    : null;
  const purchaseUnitId = existingProduct?.product.qu_id_purchase ??
    (creationMode === CreationMode.Link
      ? linkedProduct?.qu_id_purchase
      : undefined) ??
    presetQuantityUnitId ??
    undefined;
  const purchaseUnit = quantityUnits.find((unit) => unit.id === purchaseUnitId);
  const purchaseUnitLabel = getUnitName(amount, purchaseUnit);
  const productName = existingProduct?.product.name || unknownProduct?.name ||
    "Unknown Product";
  const stockText = existingProduct
    ? `📊 Current stock: ${existingProduct.stock.amount} ${existingProduct.quantityUnit.name}`
    : undefined;
  const noOwnStockProductName = existingProduct?.product.no_own_stock === 1
    ? existingProduct.product.name
    : creationMode === CreationMode.Link && linkedProduct?.no_own_stock === 1
    ? linkedProduct.name
    : null;

  return (
    <PurchaseModal
      title={`Purchase: ${productName}`}
      subtitle={stockText}
      onClose={onClear}
    >
      {/* Amount Input */}
      <div className="purchase-controls__field purchase-controls__field--vertical">
        <label>Amount:</label>
        {(() => {
          const imageNode = existingProduct
            ? (
              <ProductImage
                product={existingProduct.product}
                size="medium"
                className="purchase-controls__product-image"
              />
            )
            : unknownProduct
            ? (
              unknownProduct.__image_url
                ? (
                  <ProductImage
                    imageUrl={unknownProduct.__image_url}
                    size="medium"
                    className="purchase-controls__product-image"
                  />
                )
                : (
                  <div className="purchase-controls__product-image purchase-controls__product-image--placeholder">
                    📦
                  </div>
                )
            )
            : null;

          return (
            <AmountInput
              amount={amount}
              onChangeAmount={setAmount}
              imageNode={imageNode}
              unitLabel={purchaseUnitLabel}
            />
          );
        })()}
      </div>

      {noOwnStockProductName && (
        <div className="purchase-controls__no-own-stock-warning" role="alert">
          <strong>No own stock</strong>
          <span>
            {noOwnStockProductName}{" "}
            is set to not keep its own stock. Choose a child product instead
            before adding stock.
          </span>
        </div>
      )}

      {/* Parent/Link Selector for Unknown Products */}
      <ParentOrLinkSelector
        unknownProduct={unknownProduct}
        creationMode={creationMode}
        onChangeCreationMode={handleCreationModeChange}
        parentProductId={parentProductId}
        onParentProductSelect={(p: Product) => {
          setParentProductId(p.id);
          setParentProductSearchTerm("");
          setLocationId(p.location_id || null);
          setProductGroupId(p.product_group_id || null);
          if (
            typeof p.default_best_before_days === "number" &&
            p.default_best_before_days !== 0
          ) {
            setExpiryDate(
              computeExpiryFromDefaultDays(p.default_best_before_days),
            );
          }
        }}
        onParentProductClear={() => {
          setParentProductId(null);
          setParentProductSearchTerm("");
        }}
        parentProductSearchTerm={parentProductSearchTerm}
        onParentProductSearchTermChange={setParentProductSearchTerm}
        linkProductId={linkProductId}
        onLinkProductSelect={(p: Product) => {
          setLinkProductId(p.id);
          setLinkProductSearchTerm("");
          setLocationId(p.location_id || null);
          if (
            typeof p.default_best_before_days === "number" &&
            p.default_best_before_days !== 0
          ) {
            setExpiryDate(
              computeExpiryFromDefaultDays(p.default_best_before_days),
            );
          }
        }}
        onLinkProductClear={() => {
          setLinkProductId(null);
          setLinkProductSearchTerm("");
        }}
        linkProductSearchTerm={linkProductSearchTerm}
        onLinkProductSearchTermChange={setLinkProductSearchTerm}
        products={products}
        aiLoading={aiLoading}
        aiSuggestion={aiSuggestion}
        aiSuggestionProductName={aiSuggestionProductName}
        aiDismissed={aiDismissed}
        onDismissAISuggestion={handleDismissAISuggestion}
      />

      {/* Common Form Fields */}
      <PurchaseFormFields
        expiryDate={expiryDate}
        onExpiryDateChange={setExpiryDate}
        onRequestPhotoCapture={onRequestPhotoCapture}
        locationId={locationId}
        onLocationIdChange={setLocationId}
        showParentSelector={false} // Parent selector is handled by ParentOrLinkSelector
      />

      {/* Product Group (for new products only) */}
      {unknownProduct &&
        (creationMode === CreationMode.Create ||
          creationMode === CreationMode.Parent) &&
        (
          <div className="purchase-controls__field">
            <label>Product Group:</label>
            <ProductGroupSelectorWithAI
              productName={unknownProduct.name}
              value={productGroupId}
              onChange={setProductGroupId}
            />
          </div>
        )}

      {/* Purchase Button */}
      <PurchaseButton
        onClick={handlePurchase}
        isLoading={isLoading}
        disabled={!expiryDate ||
          (creationMode === CreationMode.Parent && !parentProductId) ||
          (creationMode === CreationMode.Link && !linkProductId) ||
          Boolean(noOwnStockProductName)}
      />
    </PurchaseModal>
  );
};

export default PurchaseControls;
